import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import mediaClientService from "../services/mediaClient.service";
import clientService from "../services/client.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";

const MediaClientManager = () => {
  const [loading, setLoading] = useState(false);
  const [mediaClients, setMediaClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({});
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [sortDirection, setSortDirection] = useState("ASC");

  // Modal and search states
  const [showCreateFromPartsKickModal, setShowCreateFromPartsKickModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [newFormData, setNewFormData] = useState({
    clientName: "",
    phone: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    postcode: "",
    country: "",
    contactPerson: "",
    parentId: "",
    allowSubClient: false,
    subAccount: "",
    storage: "",
    enablePhotobase: true,
    enableInventory: true,
    aiEnhance: false,
    shareInventory: false,
    pdContactPerson: "",
    pdContactPhone: "",
    pdContactEmail: "",
    inventorySources: [0, 2, 4, 6, 8, 10]
  });
  const [newLoading, setNewLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [postcodeError, setPostcodeError] = useState("");
  const [showS3SyncModal, setShowS3SyncModal] = useState(false);
  const [currentS3SyncClientId, setCurrentS3SyncClientId] = useState(null);
  const [s3SyncData, setS3SyncData] = useState({});
  const [s3SyncLoading, setS3SyncLoading] = useState({});
  const [showMediaSettingsModal, setShowMediaSettingsModal] = useState(false);
  const [currentMediaSettingsClientId, setCurrentMediaSettingsClientId] = useState(null);
  const [currentMediaSettingsStorage, setCurrentMediaSettingsStorage] = useState("");
  const [mediaSettingsLoading, setMediaSettingsLoading] = useState({});
  const [mediaSettingsData, setMediaSettingsData] = useState({
    personalFolderEnabled: false,
    subFolderEnabled: false,
    subFoldersInput: "",
    photoPixelLimitEnabled: false,
    photoPixelLimit: "",
    photoSizeLimitEnabled: false,
    photoSizeLimit: "",
    photoTimeStampEnabled: true,
    audioNoteLength: "60",
    rootFolder: ""
  });
  const [mediaSettingsErrors, setMediaSettingsErrors] = useState({
    folderError: "",
    photoPixelLimitError: "",
    photoSizeLimitError: "",
    audioNoteLengthError: "",
    rootFolderError: ""
  });
  const [eligibleParents, setEligibleParents] = useState([]);
  const [loadingEligibleParents, setLoadingEligibleParents] = useState(false);
  const [hasChildClients, setHasChildClients] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [listSearchTerm, setListSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [currentResourcesClientId, setCurrentResourcesClientId] = useState(null);
  const [resourcesData, setResourcesData] = useState(null);
  const [isCurrentResourcesClientChild, setIsCurrentResourcesClientChild] = useState(false);
  const [loadingResources, setLoadingResources] = useState({});
  const [editLoading, setEditLoading] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmCountdown, setDeleteConfirmCountdown] = useState(10);
  const [deleteConfirmStage, setDeleteConfirmStage] = useState(1); // 1 = first confirmation, 2 = second confirmation
  const [deleteCountdownInterval, setDeleteCountdownInterval] = useState(null);
  const [qrCodeLoading, setQrCodeLoading] = useState({});
  const [parentFilterId, setParentFilterId] = useState(null);
  const [parentFilterName, setParentFilterName] = useState(null);
  const [pkStorage, setPkStorage] = useState("");
  const [statusUpdateLoading, setStatusUpdateLoading] = useState({});
  const [creatingPkClientId, setCreatingPkClientId] = useState(null);

  const { user: currentUser } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!currentUser) return;

    const fetchMediaClients = async () => {
      setLoading(true);
      setError(null);
      try {
        let response;
        if (parentFilterId) {
          // Fetch children of parent client
          response = await mediaClientService.getChildrenByParentId(
            parentFilterId,
            currentPage,
            pageSize,
            sortDirection
          );
        } else {
          // Fetch all clients with optional search
          response = await mediaClientService.getMediaClients(
            currentPage,
            pageSize,
            activeSearchTerm,
            sortDirection
          );
        }
        const clients = response.data.content;
        // Debug: Log sub clients to verify parentHasCloudResources is received
        clients.forEach(client => {
          if (client.parentId) {
            console.log('Received sub client:', {
              mediaClientId: client.mediaClientId,
              parentId: client.parentId,
              parentHasCloudResources: client.parentHasCloudResources,
              type: typeof client.parentHasCloudResources
            });
          }
        });
        setMediaClients(clients);
        setPagination({
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          currentPage: response.data.number,
          pageSize: response.data.size,
          isFirst: response.data.first,
          isLast: response.data.last
        });
      } catch (error) {
        const errorMessage =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        console.error("Error fetching media clients:", error);
        setError(errorMessage);
        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load media clients" });
      } finally {
        setLoading(false);
      }
    };

    fetchMediaClients();
  }, [currentPage, pageSize, currentUser, activeSearchTerm, sortDirection, parentFilterId]);

  // Load countries when component mounts
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoadingCountries(true);
        const response = await mediaClientService.getAllCountries();
        setCountries(response.data);
      } catch (error) {
        console.error("Error fetching countries:", error);
        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load countries" });
      } finally {
        setLoadingCountries(false);
      }
    };

    if (currentUser) {
      fetchCountries();
    }
  }, [currentUser]);

  // Cleanup countdown interval on unmount
  useEffect(() => {
    return () => {
      if (deleteCountdownInterval) {
        clearInterval(deleteCountdownInterval);
      }
    };
  }, [deleteCountdownInterval]);

  // Load provinces when country changes
  useEffect(() => {
    const fetchProvinces = async () => {
      if (!newFormData.country) {
        setProvinces([]);
        setNewFormData({ ...newFormData, province: "" });
        return;
      }

      try {
        setLoadingProvinces(true);
        const response = await mediaClientService.getProvincesByCountry(newFormData.country);
        setProvinces(response.data);
        // Reset province if current selection is not valid for new country
        if (newFormData.province && !response.data.find(p => p.provCode === newFormData.province)) {
          setNewFormData({ ...newFormData, province: "" });
        }
      } catch (error) {
        console.error("Error fetching provinces:", error);
        setProvinces([]);
        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load provinces" });
      } finally {
        setLoadingProvinces(false);
      }
    };

    if (showNewModal && currentUser) {
      fetchProvinces();
    }
  }, [newFormData.country, showNewModal, currentUser]);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (event) => {
    const newSize = parseInt(event.target.value);
    setPageSize(newSize);
    setCurrentPage(0);
  };

  const handleFeatureToggle = async (mediaClientId, featureName, newValue) => {
    // Optimistic update
    const previousClients = [...mediaClients];
    setMediaClients(clients => 
      clients.map(c => 
        c.mediaClientId === mediaClientId 
          ? { ...c, [featureName]: newValue } 
          : c
      )
    );

    try {
      const payload = {};
      payload[featureName] = newValue;
      await mediaClientService.updateClientFeatures(mediaClientId, payload);
    } catch (error) {
      // Revert on failure
      setMediaClients(previousClients);
      console.error("Error updating feature:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to update feature" });
    }
  };

  const getStatusBadge = (statusName) => {
    if (!statusName) {
      return <span className="badge bg-secondary">Unknown</span>;
    }
    const statusLower = statusName.toLowerCase();
    if (statusLower === "active") {
      return <span className="badge bg-success">Active</span>;
    } else if (statusLower === "disabled") {
      return <span className="badge bg-danger">Disabled</span>;
    } else if (statusLower === "pending") {
      return <span className="badge bg-warning">Pending</span>;
    } else if (statusLower === "suspend") {
      return <span className="badge bg-secondary">Suspended</span>;
    }
    return <span className="badge bg-secondary">{statusName}</span>;
  };

  const handleEdit = async (mediaClientId) => {
    if (editLoading[mediaClientId]) return;

    setEditLoading({ ...editLoading, [mediaClientId]: true });
    setLoadingEligibleParents(true);
    setValidationErrors({});
    try {
      const [clientResponse, parentsResponse, hasChildrenResponse] = await Promise.all([
        mediaClientService.getMediaClientById(mediaClientId),
        mediaClientService.getEligibleParentClients(),
        mediaClientService.hasChildClients(mediaClientId)
      ]);

      const clientData = clientResponse.data;
      console.log("Edit Client Data:", clientData);
      const allParents = parentsResponse.data || [];

      // Exclude the current client from eligible parents list
      const filteredParents = allParents.filter(
        (parent) => parent.mediaClientId !== mediaClientId
      );

      setEditingClient(clientData);
      setEligibleParents(filteredParents);
      const hasChildren = hasChildrenResponse.data || false;
      setHasChildClients(hasChildren);

      // If client has children, ensure allowSubClient is true
      const allowSubClientValue = hasChildren ? true : (clientData.allowSubClient || false);

      setEditFormData({
        clientName: clientData.clientName || "",
        phone: clientData.phone || "",
        email: clientData.email || "",
        address1: clientData.address1 || "",
        address2: clientData.address2 || "",
        city: clientData.city || "",
        province: clientData.province || "",
        postcode: clientData.postcode || "",
        country: clientData.country || "",
        contactPerson: clientData.contactPerson || "",
        parentId: clientData.parentId || "",
        allowSubClient: allowSubClientValue,
        subAccount: clientData.subAccount || "",
        storage: clientData.storage || "",
        shareInventory: clientData.shareInventory || false,
        aiEnhance: clientData.aiEnhance || false,
        pdContactPerson: clientData.pdContactPerson || "",
        pdContactPhone: clientData.pdContactPhone || "",
        pdContactEmail: clientData.pdContactEmail || "",
        inventorySources: clientData.inventorySources || []
      });
      setShowEditModal(true);
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to load client details";
      console.error("Error loading client:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
    } finally {
      setEditLoading({ ...editLoading, [mediaClientId]: false });
      setLoadingEligibleParents(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingClient(null);
    setEditFormData({});
    setHasChildClients(false);
    setValidationErrors({});
    setEligibleParents([]);
  };

  const handleCloseNewModal = () => {
    setShowNewModal(false);
    setNewFormData({
      clientName: "",
      phone: "",
      email: "",
      address1: "",
      address2: "",
      city: "",
      province: "",
      postcode: "",
      country: "",
      contactPerson: "",
      parentId: "",
      allowSubClient: false,
      subAccount: "",
      storage: "",
      enablePhotobase: true,
      enableInventory: true,
      aiEnhance: false,
      shareInventory: false,
      pdContactPerson: "",
      pdContactPhone: "",
      pdContactEmail: "",
      inventorySources: [0, 2, 4, 6, 8, 10]
    });
    setValidationErrors({});
    setEligibleParents([]);
    setPostcodeError("");
    setProvinces([]);
  };

  const validatePostcode = (postcode, countryCode) => {
    if (!postcode || !countryCode) {
      return "";
    }

    // Simple client-side validation patterns
    const patterns = {
      CAN: /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ]( )?\d[ABCEGHJKLMNPRSTVWXYZ]\d$/i,
      USA: /^\d{5}(-\d{4})?$/,
      US: /^\d{5}(-\d{4})?$/,
      GBR: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
      GB: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
      AUS: /^\d{4}$/,
      AU: /^\d{4}$/
    };

    const upperCountry = countryCode.toUpperCase();
    const pattern = patterns[upperCountry];

    if (pattern) {
      if (!pattern.test(postcode.trim())) {
        const messages = {
          CAN: "Invalid Canadian postal code format (e.g., A1A 1A1)",
          USA: "Invalid US ZIP code format (e.g., 12345 or 12345-6789)",
          US: "Invalid US ZIP code format (e.g., 12345 or 12345-6789)",
          GBR: "Invalid UK postcode format (e.g., SW1A 1AA)",
          GB: "Invalid UK postcode format (e.g., SW1A 1AA)",
          AUS: "Invalid Australian postcode format (4 digits)",
          AU: "Invalid Australian postcode format (4 digits)"
        };
        return messages[upperCountry] || "Invalid postcode format";
      }
    }

    return "";
  };

  const handleCountryChange = (countryCode) => {
    setNewFormData({
      ...newFormData,
      country: countryCode,
      province: "", // Reset province when country changes
      postcode: "" // Reset postcode validation
    });
    setPostcodeError("");
  };

  const handlePostcodeChange = (postcode) => {
    setNewFormData({ ...newFormData, postcode: postcode });
    if (newFormData.country) {
      const error = validatePostcode(postcode, newFormData.country);
      setPostcodeError(error);
    }
  };

  const handleConfigureS3Sync = async (mediaClientId) => {
    if (s3SyncLoading[mediaClientId]) return;

    setS3SyncLoading({ ...s3SyncLoading, [mediaClientId]: true });
    try {
      const response = await mediaClientService.getClientS3Sync(mediaClientId);
      setS3SyncData({
        storageSize: response.data.storageSize || 100,
        auditDownload: response.data.auditDownload !== undefined ? response.data.auditDownload : true,
        maxFileSize: response.data.maxFileSize || null,
        imageOnly: response.data.imageOnly !== undefined ? response.data.imageOnly : false,
        scheduleSyncCloud: response.data.scheduleSyncCloud || "",
        scheduleSyncNetwork: response.data.scheduleSyncNetwork || "",
        scheduleSync: response.data.scheduleSync || "",
        syncIgnoreTimeStamp: response.data.syncIgnoreTimeStamp !== undefined ? response.data.syncIgnoreTimeStamp : false,
        syncIgnoreSize: response.data.syncIgnoreSize !== undefined ? response.data.syncIgnoreSize : false,
        manualSyncEnabled: response.data.manualSyncEnabled !== undefined ? response.data.manualSyncEnabled : true,
        enableDebugLogging: response.data.enableDebugLogging !== undefined ? response.data.enableDebugLogging : false
      });
      setCurrentS3SyncClientId(mediaClientId);
      setShowS3SyncModal(true);
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to load S3 sync configuration";
      console.error("Error loading S3 sync configuration:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
    } finally {
      setS3SyncLoading({ ...s3SyncLoading, [mediaClientId]: false });
    }
  };

  const handleCloseS3SyncModal = () => {
    setShowS3SyncModal(false);
    setCurrentS3SyncClientId(null);
    setS3SyncData({});
  };

  const handleOpenMediaSettings = async (mediaClientId) => {
    if (mediaSettingsLoading[mediaClientId]) return;

    setMediaSettingsLoading({ ...mediaSettingsLoading, [mediaClientId]: true });
    try {
      const response = await clientService.getPhotoBaseMediaSettings(mediaClientId);
      const data = response.data;

      let subFoldersArr = [];
      if (Array.isArray(data.subFolders)) {
        subFoldersArr = data.subFolders;
      } else if (typeof data.subFolders === 'string') {
        subFoldersArr = data.subFolders.split(';').map(s => s.trim()).filter(Boolean);
      }

      setMediaSettingsData({
        personalFolderEnabled: data.personalFolderEnabled || false,
        subFolderEnabled: subFoldersArr.length > 0,
        subFoldersInput: subFoldersArr.join(';'),
        photoPixelLimitEnabled: data.photoPixelLimit !== null && data.photoPixelLimit !== undefined,
        photoPixelLimit: data.photoPixelLimit ? data.photoPixelLimit.toString() : "",
        photoSizeLimitEnabled: data.photoSizeLimit !== null && data.photoSizeLimit !== undefined,
        photoSizeLimit: data.photoSizeLimit ? data.photoSizeLimit.toString() : "",
        photoTimeStampEnabled: data.photoTimeStampEnabled !== null && data.photoTimeStampEnabled !== undefined
          ? data.photoTimeStampEnabled : true,
        audioNoteLength: data.audioNoteLength !== null && data.audioNoteLength !== undefined
          ? data.audioNoteLength.toString() : "60",
        rootFolder: data.rootFolder || ""
      });
      setMediaSettingsErrors({
        folderError: "",
        photoPixelLimitError: "",
        photoSizeLimitError: "",
        audioNoteLengthError: "",
        rootFolderError: ""
      });
      const client = mediaClients.find(c => c.mediaClientId === mediaClientId);
      setCurrentMediaSettingsStorage(client ? client.storage : "");
      setCurrentMediaSettingsClientId(mediaClientId);
      setShowMediaSettingsModal(true);
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to load media settings";
      console.error("Error loading media settings:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
    } finally {
      setMediaSettingsLoading({ ...mediaSettingsLoading, [mediaClientId]: false });
    }
  };

  const handleCloseMediaSettingsModal = () => {
    setShowMediaSettingsModal(false);
    setCurrentMediaSettingsClientId(null);
    setCurrentMediaSettingsStorage("");
    setMediaSettingsData({
      personalFolderEnabled: false,
      subFolderEnabled: false,
      subFoldersInput: "",
      photoPixelLimitEnabled: false,
      photoPixelLimit: "",
      photoSizeLimitEnabled: false,
      photoSizeLimit: "",
      photoTimeStampEnabled: true,
      audioNoteLength: "60",
      rootFolder: ""
    });
    setMediaSettingsErrors({
      folderError: "",
      photoPixelLimitError: "",
      photoSizeLimitError: "",
      audioNoteLengthError: "",
      rootFolderError: ""
    });
  };

  const copyTextToClipboard = async (value, label) => {
    if (!value) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = value;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }

      toast.success(`${label} copied to clipboard`);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error(`Failed to copy ${label}`);
    }
  };

  const renderCopyableResourceField = (label, value) => (
    <div className="mb-3">
      <label className="form-label fw-bold">{label}:</label>
      <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
        <code className="flex-grow-1 text-break">{value || "—"}</code>
        <button
          type="button"
          className="btn btn-sm border-0 bg-transparent text-secondary flex-shrink-0"
          onClick={() => copyTextToClipboard(value, label)}
          disabled={!value}
          title={`Copy ${label}`}
          aria-label={`Copy ${label}`}
          style={{ boxShadow: "none" }}
        >
          <i className="bi bi-copy"></i>
        </button>
      </div>
    </div>
  );

  const handleSaveS3Sync = async () => {
    if (!currentS3SyncClientId) return;

    // Validate: if ignore size is false, max file size must be set
    if (!s3SyncData.syncIgnoreSize && (!s3SyncData.maxFileSize || s3SyncData.maxFileSize <= 0)) {
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Max File Size is required when Ignore Size is disabled" });
      return;
    }

    setS3SyncLoading({ ...s3SyncLoading, [currentS3SyncClientId]: true });
    try {
      await mediaClientService.updateClientS3Sync(currentS3SyncClientId, s3SyncData);
      toast.success("S3 sync configuration updated successfully");
      handleCloseS3SyncModal();
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to update S3 sync configuration";
      console.error("Error updating S3 sync configuration:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
    } finally {
      setS3SyncLoading({ ...s3SyncLoading, [currentS3SyncClientId]: false });
    }
  };

  const handleSaveEdit = async () => {
    if (!editingClient) return;

    let errors = {};
    const hasParentId = editFormData.parentId && editFormData.parentId.trim() !== "";
    const allowSubClient = editFormData.allowSubClient === true;

    if (hasParentId && allowSubClient) {
      errors.allowSubClient = "Cannot set both parent ID and allow sub client.";
    }

    if (hasParentId) {
      if (!editFormData.subAccount || editFormData.subAccount.trim() === "") {
        errors.subAccount = "Sub account name is required when parent ID is set.";
      } else {
        const subAccount = editFormData.subAccount.trim();
        if (subAccount.length > 20) {
          errors.subAccount = "Sub account name cannot exceed 20 characters.";
        } else if (!/^[a-z0-9-]+$/.test(subAccount)) {
          errors.subAccount = "Sub account name can only contain lowercase letters (a-z), numbers (0-9), and hyphens (-).";
        }
      }
    }

    if (editFormData.shareInventory) {
      if (!editFormData.pdContactPerson || editFormData.pdContactPerson.trim() === "") {
        errors.pdContactPerson = "Required when sharing inventory.";
      }
      if (!editFormData.pdContactPhone || editFormData.pdContactPhone.trim() === "") {
        errors.pdContactPhone = "Required when sharing inventory.";
      }
      if (!editFormData.pdContactEmail || editFormData.pdContactEmail.trim() === "") {
        errors.pdContactEmail = "Required when sharing inventory.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please fill in all required fields." });
      return;
    }

    setValidationErrors({});
    const mediaClientId = editingClient.mediaClientId;
    setEditLoading({ ...editLoading, [mediaClientId]: true });
    try {
      // Ensure parentId is always explicitly provided: value if selected, empty string if not
      const updateData = {
        ...editFormData,
        parentId: editFormData.parentId && editFormData.parentId.trim() !== ""
          ? editFormData.parentId.trim()
          : ""
      };
      const updateResponse = await mediaClientService.updateMediaClient(mediaClientId, updateData);
      const updatedClient = updateResponse?.data;
      if (updatedClient) {
        setEditingClient(updatedClient);
        setMediaClients((clients) =>
          clients.map((client) =>
            client.mediaClientId === mediaClientId
              ? { ...client, aiEnhance: updatedClient.aiEnhance }
              : client
          )
        );
      }
      toast.success("Client updated successfully");
      handleCloseEditModal();
      // Refresh the media clients list
      const response = await mediaClientService.getMediaClients(
        currentPage,
        pageSize,
        activeSearchTerm,
        sortDirection
      );
      setMediaClients(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to update client";
      console.error("Error updating client:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      if (errorMessage.includes("Cannot set both parent ID and allow sub client")) {
        setValidationErrors({ allowSubClient: errorMessage });
      }
    } finally {
      setEditLoading({ ...editLoading, [mediaClientId]: false });
    }
  };

  const handleSaveNew = async () => {
    let errors = {};
    const hasParentId = newFormData.parentId && newFormData.parentId.trim() !== "";
    const allowSubClient = newFormData.allowSubClient === true;

    if (hasParentId && allowSubClient) {
      errors.allowSubClient = "Cannot set both parent ID and allow sub client.";
    }

    if (hasParentId) {
      if (!newFormData.subAccount || newFormData.subAccount.trim() === "") {
        errors.subAccount = "Sub account name is required when parent ID is set.";
      } else {
        const subAccount = newFormData.subAccount.trim();
        if (subAccount.length > 20) {
          errors.subAccount = "Sub account name cannot exceed 20 characters.";
        } else if (!/^[a-z0-9-]+$/.test(subAccount)) {
          errors.subAccount = "Sub account name can only contain lowercase letters (a-z), numbers (0-9), and hyphens (-).";
        }
      }
    }

    if (newFormData.shareInventory) {
      if (!newFormData.pdContactPerson || newFormData.pdContactPerson.trim() === "") {
        errors.pdContactPerson = "Required when sharing inventory.";
      }
      if (!newFormData.pdContactPhone || newFormData.pdContactPhone.trim() === "") {
        errors.pdContactPhone = "Required when sharing inventory.";
      }
      if (!newFormData.pdContactEmail || newFormData.pdContactEmail.trim() === "") {
        errors.pdContactEmail = "Required when sharing inventory.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please correctly fill in all required fields." });
      return;
    }

    setValidationErrors({});
    setNewLoading(true);
    try {
      // Ensure parentId is always explicitly provided: value if selected, empty string if not
      const createData = {
        ...newFormData,
        parentId: newFormData.parentId && newFormData.parentId.trim() !== ""
          ? newFormData.parentId.trim()
          : ""
      };
      await mediaClientService.createMediaClient(createData);
      toast.success("Media client created successfully");
      handleCloseNewModal();
      // Refresh the media clients list
      const response = await mediaClientService.getMediaClients(
        currentPage,
        pageSize,
        activeSearchTerm,
        sortDirection
      );
      setMediaClients(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to create client";
      console.error("Error creating client:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      if (errorMessage.includes("Cannot set both parent ID and allow sub client")) {
        setValidationErrors({ allowSubClient: errorMessage });
      }
    } finally {
      setNewLoading(false);
    }
  };

  const handleStatusUpdate = async (mediaClientId, newStatusId) => {
    setStatusUpdateLoading({ ...statusUpdateLoading, [mediaClientId]: newStatusId });
    try {
      await mediaClientService.updateClientStatus(mediaClientId, newStatusId);
      toast.success("Client status updated successfully");
      // Refresh the media clients list
      const response = await mediaClientService.getMediaClients(
        currentPage,
        pageSize,
        activeSearchTerm,
        sortDirection
      );
      setMediaClients(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to update client status";
      console.error("Error updating client status:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
    } finally {
      setStatusUpdateLoading({ ...statusUpdateLoading, [mediaClientId]: null });
    }
  };

  const handleSendQRCode = async (mediaClientId, clientName) => {
    if (qrCodeLoading[mediaClientId]) return;

    setQrCodeLoading({ ...qrCodeLoading, [mediaClientId]: true });
    try {
      await mediaClientService.sendQRCode(mediaClientId);
      toast.success(`QR Code sent to support for ${clientName || mediaClientId}`);
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to send QR code";
      console.error("Error sending QR code:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
    } finally {
      setQrCodeLoading({ ...qrCodeLoading, [mediaClientId]: false });
    }
  };

  const handleCreateFromPartsKick = () => {
    setShowCreateFromPartsKickModal(true);
    setSearchTerm("");
    setSearchResults([]);
    setSearchError(null);
    setPkStorage("");
  };

  const handleCloseModal = () => {
    setShowCreateFromPartsKickModal(false);
    setSearchTerm("");
    setSearchResults([]);
    setSearchError(null);
    setPkStorage("");
  };

  const handleSearchClients = async () => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      setSearchError("Please enter a search term");
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    try {
      const response = await mediaClientService.searchClients(searchTerm.trim());
      setSearchResults(response.data);
      if (response.data.length === 0) {
        setSearchError("No clients found matching your search");
      }
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to search clients";
      console.error("Error searching clients:", error);
      setSearchError(errorMessage);
      setSearchResults([]);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to search clients" });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchClients();
    }
  };

  return (
    <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: '100vh' }}>
      <div className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4">
        <div className="card-body p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Media Client Manager</h2>
        <div>
          <button
            className="btn btn-primary rounded-pill px-4 shadow-sm me-2"
            onClick={handleCreateFromPartsKick}
          >
            Create from PartsKick
          </button>
          <button
            className="btn btn-secondary rounded-pill px-4 shadow-sm"
            onClick={async () => {
              setLoadingEligibleParents(true);
              setValidationErrors({});
              try {
                const response = await mediaClientService.getEligibleParentClients();
                setEligibleParents(response.data || []);
                setShowNewModal(true);
              } catch (error) {
                console.error("Error loading eligible parents:", error);
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to load eligible parent clients" });
                setShowNewModal(true); // Still show the modal
              } finally {
                setLoadingEligibleParents(false);
              }
            }}
          >
            New
          </button>
        </div>
      </div>

      {/* Search Bar or Parent Filter */}
      <div className="mb-3">
        <div className="row">
          <div className="col-md-6">
            {parentFilterId ? (
              // Parent Filter Mode
              <div className="d-flex align-items-center">
                <div className="badge bg-info text-dark p-2 me-2">
                  Sub Account of <strong>{parentFilterName || parentFilterId}</strong>
                </div>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  type="button"
                  onClick={() => {
                    setParentFilterId(null);
                    setParentFilterName(null);
                    setCurrentPage(0);
                    setActiveSearchTerm("");
                    setListSearchTerm("");
                  }}
                  title="Close filter"
                >
                  ✕ Close Filter
                </button>
              </div>
            ) : (
              // Normal Search Mode
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by client name or client ID..."
                  value={listSearchTerm}
                  onChange={(e) => setListSearchTerm(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      setActiveSearchTerm(listSearchTerm);
                      setCurrentPage(0);
                    }
                  }}
                />
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    setActiveSearchTerm(listSearchTerm);
                    setCurrentPage(0);
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Searching...
                    </>
                  ) : (
                    "Search"
                  )}
                </button>
                {activeSearchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => {
                      setListSearchTerm("");
                      setActiveSearchTerm("");
                      setCurrentPage(0);
                    }}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
            {activeSearchTerm && !parentFilterId && (
              <small className="text-muted mt-1 d-block">
                Searching for: <strong>"{activeSearchTerm}"</strong>
              </small>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {typeof error === "string"
            ? error
            : error.hasOwnProperty("message")
              ? error.message
              : "Something went wrong"}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <label htmlFor="pageSize" className="me-2">
            Items per page:
          </label>
          <select
            id="pageSize"
            className="form-select"
            style={{ width: "auto" }}
            value={pageSize}
            onChange={handlePageSizeChange}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <div>
          {loading && (
            <span className="spinner-border spinner-border-sm text-primary me-2"></span>
          )}
          <span className="text-muted">
            Showing {mediaClients.length > 0 ? currentPage * pageSize + 1 : 0} to{" "}
            {Math.min((currentPage + 1) * pageSize, pagination.totalElements || 0)} of{" "}
            {pagination.totalElements || 0} entries
          </span>
        </div>
      </div>

      {loading && mediaClients.length === 0 ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : mediaClients.length === 0 ? (
        <div className="alert alert-info" role="alert">
          No media clients found.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-dark">
              <tr>
                <th
                  style={{ cursor: "pointer", userSelect: "none" }}
                  onClick={() => {
                    setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
                    setCurrentPage(0);
                  }}
                  title="Click to sort by client name"
                >
                  Client Name {sortDirection === "ASC" ? "↑" : "↓"}
                </th>
                <th>Client Type</th>
                <th>Client Key</th>
                <th>Cloud Resources</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mediaClients.map((client) => (
                <tr key={client.mediaClientId}>
                  <td>
                    <div className="fw-semibold">
                      {client.clientName || <span className="text-muted">—</span>}
                    </div>
                    <div className="text-muted small mt-1 d-flex align-items-center gap-1">
                      <code>{client.mediaClientId}</code>
                      <button
                        type="button"
                        className="btn btn-sm border-0 bg-transparent text-secondary p-0"
                        onClick={() => copyTextToClipboard(client.mediaClientId, "Media Client ID")}
                        disabled={!client.mediaClientId}
                        title="Copy Media Client ID"
                        aria-label="Copy Media Client ID"
                        style={{ boxShadow: "none", lineHeight: 1 }}
                      >
                        <i className="bi bi-copy"></i>
                      </button>
                    </div>
                    {client.parentName && client.parentName.trim() !== "" && (
                      <div className="text-muted small mt-1">
                        <span className="me-1">P:</span>
                        {client.parentName}
                      </div>
                    )}
                    {client.subAccount && client.subAccount.trim() !== "" && (
                      <div className="small mt-1">
                        <span className="badge bg-secondary" style={{ fontSize: "0.75rem" }}>
                          {client.subAccount}
                        </span>
                      </div>
                    )}
                    {client.childCount !== null && client.childCount !== undefined && client.childCount > 0 && (
                      <div
                        className="text-info small mt-1"
                        style={{ cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => {
                          setParentFilterId(client.mediaClientId);
                          setParentFilterName(client.clientName);
                          setCurrentPage(0);
                          setActiveSearchTerm("");
                          setListSearchTerm("");
                        }}
                        title="Click to view children"
                      >
                        C: ({client.childCount})
                      </div>
                    )}
                  </td>
                  <td>
                    {client.isPkClient && (
                      <div className="mb-2">
                        <span className="badge bg-primary">PartsKick Client</span>
                      </div>
                    )}
                    <div className="d-flex align-items-center justify-content-between mb-2" style={{ width: '200px' }}>
                      <label className="form-check-label me-2" htmlFor={`enablePhotobase-${client.mediaClientId}`}>
                        <span className="badge bg-secondary" style={{ width: '120px' }}>PhotoBase Client</span>
                      </label>
                      <div className="form-check form-switch mb-0 pb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`enablePhotobase-${client.mediaClientId}`}
                          checked={client.enablePhotobase ?? true}
                          onChange={(e) => handleFeatureToggle(client.mediaClientId, 'enablePhotobase', e.target.checked)}
                        />
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between" style={{ width: '200px' }}>
                      <label className="form-check-label me-2" htmlFor={`enableInventory-${client.mediaClientId}`}>
                        <span className="badge bg-info text-dark" style={{ width: '120px' }}>Inventory Client</span>
                      </label>
                      <div className="form-check form-switch mb-0 pb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`enableInventory-${client.mediaClientId}`}
                          checked={client.enableInventory ?? true}
                          onChange={(e) => handleFeatureToggle(client.mediaClientId, 'enableInventory', e.target.checked)}
                        />
                      </div>
                    </div>
                  </td>
                  <td>
                    {client.clientKey ? (
                      <code>{client.clientKey}</code>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <div className="d-flex flex-column align-items-center gap-2">
                      <span className="badge bg-info text-dark">
                        {client.storage || "Default (PartsKick)"}
                      </span>
                      {client.parentId ? (
                      // Child client (has parentId)
                      client.hasCloudResources ? (
                        // Child client with cloud resources: show View button
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={async () => {
                            if (loadingResources[client.mediaClientId]) return;

                            setLoadingResources({ ...loadingResources, [client.mediaClientId]: true });
                            try {
                              const resources = await mediaClientService.getClientS3Resources(client.mediaClientId);
                              setResourcesData(resources.data);
                              setCurrentResourcesClientId(client.mediaClientId);
                              setIsCurrentResourcesClientChild(!!(client.parentId && client.parentId.trim() !== ''));
                              setShowResourcesModal(true);
                            } catch (error) {
                              const errorMessage =
                                (error.response && error.response.data) ||
                                error.message ||
                                "Failed to load cloud resources";
                              console.error("Error loading cloud resources:", error);
                              EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
                            } finally {
                              setLoadingResources({ ...loadingResources, [client.mediaClientId]: false });
                            }
                          }}
                          disabled={loadingResources[client.mediaClientId]}
                        >
                          {loadingResources[client.mediaClientId] ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Loading...
                            </>
                          ) : (
                            "View"
                          )}
                        </button>
                      ) : client.parentHasCloudResources === true ? (
                        // Child client without resources but parent has resources: show Generate button
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={async () => {
                            if (actionLoading[client.mediaClientId]) return;

                            setActionLoading({ ...actionLoading, [client.mediaClientId]: true });
                            try {
                              await mediaClientService.generateSubClientCloudResources(client.mediaClientId);
                              toast.success("Cloud resources generated successfully");
                              // Refresh the media clients list
                              const response = await mediaClientService.getMediaClients(
                                currentPage,
                                pageSize,
                                activeSearchTerm,
                                sortDirection
                              );
                              const clients = response.data.content;
                              setMediaClients(clients);
                              setPagination({
                                totalElements: response.data.totalElements,
                                totalPages: response.data.totalPages,
                                currentPage: response.data.number,
                                pageSize: response.data.size,
                                isFirst: response.data.first,
                                isLast: response.data.last
                              });
                            } catch (error) {
                              const errorMessage =
                                (error.response && error.response.data) ||
                                error.message ||
                                "Failed to generate cloud resources";
                              console.error("Error generating cloud resources:", error);
                              EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
                            } finally {
                              setActionLoading({ ...actionLoading, [client.mediaClientId]: false });
                            }
                          }}
                          disabled={actionLoading[client.mediaClientId]}
                        >
                          {actionLoading[client.mediaClientId] ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Generating...
                            </>
                          ) : (
                            "Generate"
                          )}
                        </button>
                      ) : (
                        // Child client without resources and parent doesn't have resources: show dash
                        <span className="text-muted">—</span>
                      )
                    ) : client.hasCloudResources ? (
                      <button
                        className="btn btn-sm btn-outline-info"
                        onClick={async () => {
                          if (loadingResources[client.mediaClientId]) return;

                          setLoadingResources({ ...loadingResources, [client.mediaClientId]: true });
                          try {
                            const response = await mediaClientService.getClientS3Resources(client.mediaClientId);
                            setResourcesData(response.data);
                            setCurrentResourcesClientId(client.mediaClientId);
                            setIsCurrentResourcesClientChild(!!(client.parentId && client.parentId.trim() !== ''));
                            setShowResourcesModal(true);
                          } catch (error) {
                            const errorMessage =
                              (error.response && error.response.data) ||
                              error.message ||
                              "Failed to load cloud resources";
                            console.error("Error loading cloud resources:", error);
                            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
                          } finally {
                            setLoadingResources({ ...loadingResources, [client.mediaClientId]: false });
                          }
                        }}
                        disabled={loadingResources[client.mediaClientId]}
                      >
                        {loadingResources[client.mediaClientId] ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Loading...
                          </>
                        ) : (
                          "View"
                        )}
                      </button>
                    ) : (
                      // Client without cloud resources and no parentId - show Generate button
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={async () => {
                          if (actionLoading[client.mediaClientId]) return;

                          setActionLoading({ ...actionLoading, [client.mediaClientId]: true });
                          try {
                            await mediaClientService.generateCloudResources(client.mediaClientId);
                            toast.success("Cloud resources generated successfully");
                            // Refresh the media clients list
                            const response = await mediaClientService.getMediaClients(
                              currentPage,
                              pageSize,
                              activeSearchTerm,
                              sortDirection
                            );
                            const clients = response.data.content;
                            setMediaClients(clients);
                            setPagination({
                              totalElements: response.data.totalElements,
                              totalPages: response.data.totalPages,
                              currentPage: response.data.number,
                              pageSize: response.data.size,
                              isFirst: response.data.first,
                              isLast: response.data.last
                            });
                          } catch (error) {
                            const errorMessage =
                              (error.response && error.response.data) ||
                              error.message ||
                              "Failed to generate cloud resources";
                            console.error("Error generating cloud resources:", error);
                            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
                          } finally {
                            setActionLoading({ ...actionLoading, [client.mediaClientId]: false });
                          }
                        }}
                        disabled={actionLoading[client.mediaClientId]}
                      >
                        {actionLoading[client.mediaClientId] ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Generating...
                          </>
                        ) : (
                          "Generate"
                        )}
                      </button>
                      )}
                    </div>
                  </td>
                  <td>{getStatusBadge(client.clientStatusName)}</td>
                  <td>
                    <div className="d-flex flex-column gap-2" style={{ minWidth: '240px' }}>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-primary flex-grow-1"
                            onClick={() => handleEdit(client.mediaClientId)}
                            disabled={editLoading[client.mediaClientId]}
                            title="Edit client"
                          >
                            {editLoading[client.mediaClientId] ? (
                              <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Loading...</>
                            ) : (
                              <><i className="bi bi-pencil-square me-1"></i> Edit</>
                            )}
                          </button>
                          <button
                            className="btn btn-sm btn-info text-white flex-grow-1"
                            onClick={() => handleConfigureS3Sync(client.mediaClientId)}
                            disabled={s3SyncLoading[client.mediaClientId]}
                            title="Configure S3 sync"
                          >
                            {s3SyncLoading[client.mediaClientId] ? (
                              <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Loading...</>
                            ) : (
                              <><i className="bi bi-cloud-arrow-up me-1"></i> Configure</>
                            )}
                          </button>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-success flex-grow-1"
                            onClick={() => handleStatusUpdate(client.mediaClientId, 1)}
                            disabled={client.clientStatusId === 1 || statusUpdateLoading[client.mediaClientId] === 1}
                            title="Activate client (set status to active)"
                          >
                            {statusUpdateLoading[client.mediaClientId] === 1 ? (
                              <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Activating...</>
                            ) : (
                              <><i className="bi bi-check-circle me-1"></i> Activate</>
                            )}
                          </button>
                          <button
                            className="btn btn-sm btn-danger flex-grow-1"
                            onClick={() => handleStatusUpdate(client.mediaClientId, 4)}
                            disabled={client.clientStatusId === 4 || statusUpdateLoading[client.mediaClientId] === 4}
                            title="Disable client (set status to disabled)"
                          >
                            {statusUpdateLoading[client.mediaClientId] === 4 ? (
                              <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Disabling...</>
                            ) : (
                              <><i className="bi bi-x-circle me-1"></i> Disable</>
                            )}
                          </button>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-warning flex-grow-1"
                            onClick={() => handleSendQRCode(client.mediaClientId, client.clientName)}
                            disabled={qrCodeLoading[client.mediaClientId] || !client.hasCloudResources}
                            title={client.hasCloudResources ? "Send QR Code to support" : "Cloud resources must be generated first"}
                          >
                            {qrCodeLoading[client.mediaClientId] ? (
                              <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Sending...</>
                            ) : (
                              <><i className="bi bi-qr-code me-1"></i> Send QR Code</>
                            )}
                          </button>
                          {client.hasCloudResources && (
                            <button
                              className="btn btn-sm btn-secondary flex-grow-1"
                              onClick={() => handleOpenMediaSettings(client.mediaClientId)}
                              disabled={mediaSettingsLoading[client.mediaClientId]}
                              title="Configure media settings"
                            >
                              {mediaSettingsLoading[client.mediaClientId] ? (
                                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Loading...</>
                              ) : (
                                <><i className="bi bi-sliders me-1"></i> Settings</>
                              )}
                            </button>
                          )}
                        </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.totalPages > 1 && (
        <nav aria-label="Media clients pagination">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${pagination.isFirst ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(0)}
                disabled={pagination.isFirst}
              >
                First
              </button>
            </li>
            <li className={`page-item ${pagination.isFirst ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={pagination.isFirst}
              >
                Previous
              </button>
            </li>
            <li className="page-item active">
              <span className="page-link">
                Page {pagination.currentPage + 1} of {pagination.totalPages}
              </span>
            </li>
            <li className={`page-item ${pagination.isLast ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={pagination.isLast}
              >
                Next
              </button>
            </li>
            <li className={`page-item ${pagination.isLast ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(pagination.totalPages - 1)}
                disabled={pagination.isLast}
              >
                Last
              </button>
            </li>
          </ul>
        </nav>
      )}

      {/* Create from PartsKick Modal */}
      {showCreateFromPartsKickModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={(e) => {
            if (e.target.className.includes("modal show d-block")) {
              handleCloseModal();
            }
          }}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Media Client from PartsKick</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="pkStorage" className="form-label">
                    Storage Type
                  </label>
                  <select
                    className="form-select"
                    id="pkStorage"
                    value={pkStorage}
                    onChange={(e) => setPkStorage(e.target.value)}
                  >
                    <option value="">Default (PartsKick)</option>
                    <option value="Google Drive">Google Drive</option>
                  </select>
                  <div className="form-text">
                    Select the storage backend for this client.
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="clientSearch" className="form-label">
                    Search Client by Name
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="clientSearch"
                      placeholder="Enter client name (wildcard match)..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={handleSearchKeyPress}
                    />
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={handleSearchClients}
                      disabled={searchLoading || !searchTerm || searchTerm.trim().length === 0}
                    >
                      {searchLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Searching...
                        </>
                      ) : (
                        "Search"
                      )}
                    </button>
                  </div>
                  {searchError && (
                    <div className="text-danger mt-2 small">{searchError}</div>
                  )}
                </div>

                {searchResults.length > 0 && (
                  <div>
                    <h6 className="mb-3">Search Results ({searchResults.length})</h6>
                    <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                      <table className="table table-sm table-hover">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Client Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchResults.map((client) => (
                            <tr key={client.oid}>
                              <td>{client.clientName}</td>
                              <td>{client.email || <span className="text-muted">—</span>}</td>
                              <td>{client.phone || <span className="text-muted">—</span>}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={async () => {
                                    setCreatingPkClientId(client.oid);
                                    try {
                                      await mediaClientService.createMediaClientFromPartsKick(client.oid, pkStorage);
                                      toast.success(`Media client created successfully from: ${client.clientName}`);
                                      handleCloseModal();
                                      // Refresh the media clients list
                                      const response = await mediaClientService.getMediaClients(
                                        currentPage,
                                        pageSize,
                                        activeSearchTerm,
                                        sortDirection
                                      );
                                      setMediaClients(response.data.content);
                                      setPagination({
                                        totalElements: response.data.totalElements,
                                        totalPages: response.data.totalPages,
                                        currentPage: response.data.number,
                                        pageSize: response.data.size,
                                        isFirst: response.data.first,
                                        isLast: response.data.last
                                      });
                                    } catch (error) {
                                      const errorMessage =
                                        (error.response && error.response.data) ||
                                        error.message ||
                                        "Failed to create media client";
                                      console.error("Error creating media client:", error);
                                      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
                                    } finally {
                                      setCreatingPkClientId(null);
                                    }
                                  }}
                                  disabled={creatingPkClientId === client.oid}
                                >
                                  {creatingPkClientId === client.oid ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                      Creating...
                                    </>
                                  ) : (
                                    "Create"
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-4 shadow-sm"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingClient && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", pointerEvents: "auto" }}
          onClick={(e) => {
            // Prevent modal from closing when clicking on backdrop
            // Only allow closing via Cancel button or X button
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Media Client</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {editingClient.isPkClient && (
                  <div className="alert alert-info mb-3">
                    <strong>PartsKick Client:</strong> This is a PartsKick client. Most fields are read-only and managed in the client table, but "Allow Sub Client" and "AI Enhance" can still be changed here.
                  </div>
                )}
                <form>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="editClientName" className="form-label">
                        Client Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="editClientName"
                        value={editFormData.clientName || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, clientName: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="editPhone" className="form-label">
                        Phone
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="editPhone"
                        value={editFormData.phone || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="editEmail" className="form-label">
                        Email
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="editEmail"
                        value={editFormData.email || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="editContactPerson" className="form-label">
                        Contact Person
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="editContactPerson"
                        value={editFormData.contactPerson || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, contactPerson: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label htmlFor="editAddress1" className="form-label">
                        Address Line 1
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="editAddress1"
                        value={editFormData.address1 || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, address1: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label htmlFor="editAddress2" className="form-label">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="editAddress2"
                        value={editFormData.address2 || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, address2: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label htmlFor="editCity" className="form-label">
                        City
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="editCity"
                        value={editFormData.city || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="editProvince" className="form-label">
                        Province/State
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="editProvince"
                        value={editFormData.province || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, province: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                    <div className="col-md-3">
                      <label htmlFor="editPostcode" className="form-label">
                        Postcode
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="editPostcode"
                        value={editFormData.postcode || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, postcode: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                    <div className="col-md-2">
                      <label htmlFor="editCountry" className="form-label">
                        Country
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="editCountry"
                        value={editFormData.country || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                        readOnly={editingClient.isPkClient}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mt-3">
                        <label className="form-check-label me-2" htmlFor="editShareInventory">
                          Share Inventory
                        </label>
                        <div className="form-check form-switch mb-0 pb-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="editShareInventory"
                            checked={editFormData.shareInventory || false}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setEditFormData({
                                ...editFormData,
                                shareInventory: isChecked
                              });
                              setValidationErrors(prev => ({
                                ...prev,
                                pdContactPerson: '',
                                pdContactPhone: '',
                                pdContactEmail: ''
                              }));
                            }}
                            disabled={editingClient.isPkClient}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mt-3">
                        <label className="form-check-label me-2" htmlFor="editAiEnhance">
                          AI Enhance
                        </label>
                        <div className="form-check form-switch mb-0 pb-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="editAiEnhance"
                            checked={editFormData.aiEnhance || false}
                            onChange={(e) => {
                              setEditFormData({ ...editFormData, aiEnhance: e.target.checked });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label htmlFor="editPdContactPerson" className="form-label">Parts Dept Contact Person {editFormData.shareInventory && <span className="text-danger">*</span>}</label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.pdContactPerson ? 'is-invalid' : ''}`}
                        id="editPdContactPerson"
                        value={editFormData.pdContactPerson || ""}
                        onChange={(e) => {
                          setEditFormData({ ...editFormData, pdContactPerson: e.target.value });
                          setValidationErrors(prev => ({...prev, pdContactPerson: ""}));
                        }}
                        readOnly={editingClient.isPkClient}
                      />
                      {validationErrors.pdContactPerson && <div className="invalid-feedback">{validationErrors.pdContactPerson}</div>}
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="editPdContactPhone" className="form-label">Parts Dept Phone {editFormData.shareInventory && <span className="text-danger">*</span>}</label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.pdContactPhone ? 'is-invalid' : ''}`}
                        id="editPdContactPhone"
                        value={editFormData.pdContactPhone || ""}
                        onChange={(e) => {
                          setEditFormData({ ...editFormData, pdContactPhone: e.target.value });
                          setValidationErrors(prev => ({...prev, pdContactPhone: ""}));
                        }}
                        readOnly={editingClient.isPkClient}
                      />
                      {validationErrors.pdContactPhone && <div className="invalid-feedback">{validationErrors.pdContactPhone}</div>}
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="editPdContactEmail" className="form-label">Parts Dept Email {editFormData.shareInventory && <span className="text-danger">*</span>}</label>
                      <input
                        type="email"
                        className={`form-control ${validationErrors.pdContactEmail ? 'is-invalid' : ''}`}
                        id="editPdContactEmail"
                        value={editFormData.pdContactEmail || ""}
                        onChange={(e) => {
                          setEditFormData({ ...editFormData, pdContactEmail: e.target.value });
                          setValidationErrors(prev => ({...prev, pdContactEmail: ""}));
                        }}
                        readOnly={editingClient.isPkClient}
                      />
                      {validationErrors.pdContactEmail && <div className="invalid-feedback">{validationErrors.pdContactEmail}</div>}
                    </div>
                  </div>

                  <div className="row mb-3 mt-3 px-3 py-3 bg-light rounded border mx-1">
                      <h6 className="fw-bold text-secondary mb-3">Inventory Sources</h6>
                      <div className="d-flex flex-wrap gap-4">
                          {[
                              { value: 0, label: 'INTERNAL' },
                              { value: 2, label: 'SAME OWNER' },
                              { value: 4, label: 'GROUP' },
                              { value: 6, label: 'PK SHARED' },
                              { value: 8, label: 'RECYCLER' },
                              { value: 10, label: 'PAP' }
                          ].map(source => (
                              <div key={source.value} className="form-check form-switch d-flex align-items-center">
                                  <input 
                                      className="form-check-input m-0" 
                                      style={{ width: '2.5em', height: '1.25em', cursor: editingClient.isPkClient ? 'default' : 'pointer' }} 
                                      type="checkbox" 
                                      id={`editChkSource_${source.value}`} 
                                      checked={editFormData.inventorySources && editFormData.inventorySources.includes(source.value)} 
                                      disabled={editingClient.isPkClient}
                                      onChange={(e) => {
                                          const checked = e.target.checked;
                                          let currentSources = editFormData.inventorySources ? [...editFormData.inventorySources] : [];
                                          if (checked && !currentSources.includes(source.value)) {
                                              currentSources.push(source.value);
                                          } else if (!checked && currentSources.includes(source.value)) {
                                              currentSources = currentSources.filter(v => v !== source.value);
                                          }
                                          setEditFormData({ ...editFormData, inventorySources: currentSources });
                                      }} 
                                  />
                                  <label className="ms-2 mb-0" htmlFor={`editChkSource_${source.value}`} style={{ cursor: editingClient.isPkClient ? 'default' : 'pointer' }}>
                                      {source.label}
                                  </label>
                              </div>
                          ))}
                      </div>
                    </div>

                  {!editingClient.isPkClient && !hasChildClients && (
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label htmlFor="editParentId" className="form-label">
                          Parent Client
                        </label>
                        {loadingEligibleParents ? (
                          <div className="form-control">
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Loading eligible parents...
                          </div>
                        ) : (
                          <select
                            className="form-control"
                            id="editParentId"
                            value={editFormData.parentId || ""}
                            onChange={(e) => {
                              const selectedParentId = e.target.value;
                              setEditFormData({
                                ...editFormData,
                                parentId: selectedParentId,
                                // Automatically set allowSubClient to false when parent is selected
                                allowSubClient: selectedParentId && selectedParentId.trim() !== "" ? false : editFormData.allowSubClient,
                                // Clear subAccount when parent is cleared
                                subAccount: selectedParentId && selectedParentId.trim() !== "" ? editFormData.subAccount : ""
                              });
                              setValidationErrors(prev => ({...prev, allowSubClient: ""}));
                            }}
                          >
                            <option value="">-- No Parent --</option>
                            {eligibleParents.map((parent) => (
                              <option key={parent.mediaClientId} value={parent.mediaClientId}>
                                {parent.clientName || parent.mediaClientId}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  )}
                  {!editingClient.isPkClient && editFormData.parentId && editFormData.parentId.trim() !== "" && (
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label htmlFor="editSubAccount" className="form-label">
                          Sub Account Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${validationErrors.subAccount ? 'is-invalid' : ''}`}
                          id="editSubAccount"
                          value={editFormData.subAccount || ""}
                          maxLength={20}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow lowercase letters, numbers, and hyphens
                            const filtered = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            if (filtered.length <= 20) {
                              setEditFormData({ ...editFormData, subAccount: filtered });
                              setValidationErrors(prev => ({...prev, subAccount: ""}));
                            }
                          }}
                          placeholder="Enter sub account name (max 20 chars: a-z, 0-9, -)"
                        />
                        {validationErrors.subAccount && <div className="invalid-feedback">{validationErrors.subAccount}</div>}
                        <small className="text-muted">Maximum 20 characters. Only lowercase letters (a-z), numbers (0-9), and hyphens (-) allowed.</small>
                      </div>
                    </div>
                  )}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="editStorage" className="form-label">
                        Storage Type
                      </label>
                      <select
                        className="form-select"
                        id="editStorage"
                        value={editFormData.storage || ""}
                        onChange={(e) => setEditFormData({ ...editFormData, storage: e.target.value })}
                        disabled={!!(editFormData.parentId && editFormData.parentId.trim() !== "")}
                      >
                        <option value="">Default (PartsKick)</option>
                        <option value="Google Drive">Google Drive</option>
                      </select>
                      {editFormData.parentId && editFormData.parentId.trim() !== "" && (
                        <div className="form-text text-muted">
                          Inherited from parent client
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div className="form-check form-switch">
                    <input
                      className={`form-check-input ${validationErrors.allowSubClient ? 'is-invalid' : ''}`}
                      type="checkbox"
                      id="editAllowSubClient"
                      checked={editFormData.allowSubClient || false}
                      onChange={(e) => {
                        setEditFormData({ ...editFormData, allowSubClient: e.target.checked });
                        setValidationErrors(prev => ({...prev, allowSubClient: ""}));
                      }}
                      disabled={!!(editFormData.parentId && editFormData.parentId.trim() !== "") || hasChildClients}
                    />
                    <label className="form-check-label" htmlFor="editAllowSubClient">
                      Allow Sub Client
                      {editFormData.parentId && editFormData.parentId.trim() !== "" && (
                        <span className="text-muted ms-2">(Disabled when parent is selected)</span>
                      )}
                      {hasChildClients && (!editFormData.parentId || editFormData.parentId.trim() === "") && (
                        <span className="text-muted ms-2">(Disabled when client has children)</span>
                      )}
                    </label>
                    {validationErrors.allowSubClient && <div className="invalid-feedback d-block">{validationErrors.allowSubClient}</div>}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={handleCloseEditModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveEdit}
                      disabled={editLoading[editingClient.mediaClientId]}
                    >
                      {editLoading[editingClient.mediaClientId] ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Client Modal */}
      {showNewModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", pointerEvents: "auto" }}
          onClick={(e) => {
            // Prevent modal from closing when clicking on backdrop
            // Only allow closing via Cancel button or X button
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Media Client</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseNewModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="newClientName" className="form-label">
                        Client Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="newClientName"
                        value={newFormData.clientName || ""}
                        onChange={(e) => setNewFormData({ ...newFormData, clientName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="newPhone" className="form-label">
                        Phone <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="newPhone"
                        value={newFormData.phone || ""}
                        onChange={(e) => setNewFormData({ ...newFormData, phone: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="newEmail" className="form-label">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="newEmail"
                        value={newFormData.email || ""}
                        onChange={(e) => setNewFormData({ ...newFormData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="newContactPerson" className="form-label">
                        Contact Person <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="newContactPerson"
                        value={newFormData.contactPerson || ""}
                        onChange={(e) => setNewFormData({ ...newFormData, contactPerson: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label htmlFor="newAddress1" className="form-label">
                        Address Line 1 <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="newAddress1"
                        value={newFormData.address1 || ""}
                        onChange={(e) => setNewFormData({ ...newFormData, address1: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label htmlFor="newAddress2" className="form-label">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="newAddress2"
                        value={newFormData.address2 || ""}
                        onChange={(e) => setNewFormData({ ...newFormData, address2: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label htmlFor="newCity" className="form-label">
                        City <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="newCity"
                        value={newFormData.city || ""}
                        onChange={(e) => setNewFormData({ ...newFormData, city: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="newCountry" className="form-label">
                        Country <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="newCountry"
                        value={newFormData.country || ""}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        required
                        disabled={loadingCountries}
                      >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                          <option key={country.countryCode} value={country.countryCode}>
                            {country.countryName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="newProvince" className="form-label">
                        Province/State <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="newProvince"
                        value={newFormData.province || ""}
                        onChange={(e) => setNewFormData({ ...newFormData, province: e.target.value })}
                        required
                        disabled={!newFormData.country || loadingProvinces}
                      >
                        <option value="">Select Province/State</option>
                        {provinces.map((province) => (
                          <option key={province.provCode} value={province.provCode}>
                            {province.provName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="newPostcode" className="form-label">
                        Postcode <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${postcodeError ? "is-invalid" : ""}`}
                        id="newPostcode"
                        value={newFormData.postcode || ""}
                        onChange={(e) => handlePostcodeChange(e.target.value)}
                        required
                        maxLength="10"
                      />
                      {postcodeError && (
                        <div className="invalid-feedback">{postcodeError}</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="newParentId" className="form-label">
                        Parent Client
                      </label>
                      {loadingEligibleParents ? (
                        <div className="form-control">
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Loading eligible parents...
                        </div>
                      ) : (
                        <select
                          className="form-control"
                          id="newParentId"
                          value={newFormData.parentId || ""}
                          onChange={(e) => {
                            const selectedParentId = e.target.value;
                            const parent = eligibleParents.find(p => p.mediaClientId === selectedParentId);
                            const inheritedStorage = parent ? (parent.storage || "") : "";

                            setNewFormData({
                              ...newFormData,
                              parentId: selectedParentId,
                              // Automatically set allowSubClient to false when parent is selected
                              allowSubClient: selectedParentId && selectedParentId.trim() !== "" ? false : newFormData.allowSubClient,
                              // Clear subAccount when parent is cleared
                              subAccount: selectedParentId && selectedParentId.trim() !== "" ? newFormData.subAccount : "",
                              // Inherit storage from parent
                              storage: selectedParentId && selectedParentId.trim() !== "" ? inheritedStorage : newFormData.storage
                            });
                            setValidationErrors(prev => ({...prev, allowSubClient: ""}));
                          }}
                        >
                          <option value="">-- No Parent --</option>
                          {eligibleParents.map((parent) => (
                            <option key={parent.mediaClientId} value={parent.mediaClientId}>
                              {parent.clientName || parent.mediaClientId}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  {newFormData.parentId && newFormData.parentId.trim() !== "" && (
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label htmlFor="newSubAccount" className="form-label">
                          Sub Account Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${validationErrors.subAccount ? 'is-invalid' : ''}`}
                          id="newSubAccount"
                          value={newFormData.subAccount || ""}
                          maxLength={20}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow lowercase letters, numbers, and hyphens
                            const filtered = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                            if (filtered.length <= 20) {
                              setNewFormData({ ...newFormData, subAccount: filtered });
                              setValidationErrors(prev => ({...prev, subAccount: ""}));
                            }
                          }}
                          placeholder="Enter sub account name (max 20 chars: a-z, 0-9, -)"
                        />
                        {validationErrors.subAccount && <div className="invalid-feedback">{validationErrors.subAccount}</div>}
                        <small className="text-muted">Maximum 20 characters. Only lowercase letters (a-z), numbers (0-9), and hyphens (-) allowed.</small>
                      </div>
                    </div>
                  )}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="newStorage" className="form-label">
                        Storage Type
                      </label>
                      <select
                        className="form-select"
                        id="newStorage"
                        value={newFormData.storage || ""}
                        onChange={(e) => setNewFormData({ ...newFormData, storage: e.target.value })}
                        disabled={!!(newFormData.parentId && newFormData.parentId.trim() !== "")}
                      >
                        <option value="">Default (PartsKick)</option>
                        <option value="Google Drive">Google Drive</option>
                      </select>
                      {newFormData.parentId && newFormData.parentId.trim() !== "" && (
                        <div className="form-text text-muted">
                          Inherited from parent client
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mt-3">
                        <label className="form-check-label me-2" htmlFor="newEnablePhotobase">
                          Enable PhotoBase
                        </label>
                        <div className="form-check form-switch mb-0 pb-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="newEnablePhotobase"
                            checked={newFormData.enablePhotobase !== false} // defaults to true
                            onChange={(e) => {
                              setNewFormData({ ...newFormData, enablePhotobase: e.target.checked });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mt-3">
                        <label className="form-check-label me-2" htmlFor="newEnableInventory">
                          Enable Inventory
                        </label>
                        <div className="form-check form-switch mb-0 pb-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="newEnableInventory"
                            checked={newFormData.enableInventory !== false} // defaults to true
                            onChange={(e) => {
                              setNewFormData({ ...newFormData, enableInventory: e.target.checked });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mt-3">
                        <label className="form-check-label me-2" htmlFor="newAiEnhance">
                          AI Enhance
                        </label>
                        <div className="form-check form-switch mb-0 pb-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="newAiEnhance"
                            checked={newFormData.aiEnhance || false}
                            onChange={(e) => {
                              setNewFormData({ ...newFormData, aiEnhance: e.target.checked });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center mt-3">
                        <label className="form-check-label me-2" htmlFor="newShareInventory">
                          Share Inventory
                        </label>
                        <div className="form-check form-switch mb-0 pb-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="newShareInventory"
                            checked={newFormData.shareInventory || false}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setNewFormData({
                                ...newFormData,
                                shareInventory: isChecked
                              });
                              setValidationErrors(prev => ({
                                ...prev,
                                pdContactPerson: '',
                                pdContactPhone: '',
                                pdContactEmail: ''
                              }));
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label htmlFor="newPdContactPerson" className="form-label">Parts Dept Contact Person {newFormData.shareInventory && <span className="text-danger">*</span>}</label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.pdContactPerson ? 'is-invalid' : ''}`}
                        id="newPdContactPerson"
                        value={newFormData.pdContactPerson || ""}
                        onChange={(e) => {
                          setNewFormData({ ...newFormData, pdContactPerson: e.target.value });
                          setValidationErrors(prev => ({...prev, pdContactPerson: ""}));
                        }}
                      />
                      {validationErrors.pdContactPerson && <div className="invalid-feedback">{validationErrors.pdContactPerson}</div>}
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="newPdContactPhone" className="form-label">Parts Dept Phone {newFormData.shareInventory && <span className="text-danger">*</span>}</label>
                      <input
                        type="text"
                        className={`form-control ${validationErrors.pdContactPhone ? 'is-invalid' : ''}`}
                        id="newPdContactPhone"
                        value={newFormData.pdContactPhone || ""}
                        onChange={(e) => {
                          setNewFormData({ ...newFormData, pdContactPhone: e.target.value });
                          setValidationErrors(prev => ({...prev, pdContactPhone: ""}));
                        }}
                      />
                      {validationErrors.pdContactPhone && <div className="invalid-feedback">{validationErrors.pdContactPhone}</div>}
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="newPdContactEmail" className="form-label">Parts Dept Email {newFormData.shareInventory && <span className="text-danger">*</span>}</label>
                      <input
                        type="email"
                        className={`form-control ${validationErrors.pdContactEmail ? 'is-invalid' : ''}`}
                        id="newPdContactEmail"
                        value={newFormData.pdContactEmail || ""}
                        onChange={(e) => {
                          setNewFormData({ ...newFormData, pdContactEmail: e.target.value });
                          setValidationErrors(prev => ({...prev, pdContactEmail: ""}));
                        }}
                      />
                      {validationErrors.pdContactEmail && <div className="invalid-feedback">{validationErrors.pdContactEmail}</div>}
                    </div>
                  </div>

                  <div className="row mb-3 mt-3 px-3 py-3 bg-light rounded border mx-1">
                    <h6 className="fw-bold text-secondary mb-3">Inventory Sources</h6>
                    <div className="d-flex flex-wrap gap-4">
                        {[
                            { value: 0, label: 'INTERNAL' },
                            { value: 2, label: 'SAME OWNER' },
                            { value: 4, label: 'GROUP' },
                            { value: 6, label: 'PK SHARED' },
                            { value: 8, label: 'RECYCLER' },
                            { value: 10, label: 'PAP' }
                        ].map(source => (
                            <div key={source.value} className="form-check form-switch d-flex align-items-center">
                                <input 
                                    className="form-check-input m-0" 
                                    style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }} 
                                    type="checkbox" 
                                    id={`newChkSource_${source.value}`} 
                                    checked={newFormData.inventorySources && newFormData.inventorySources.includes(source.value)} 
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        let currentSources = newFormData.inventorySources ? [...newFormData.inventorySources] : [];
                                        if (checked && !currentSources.includes(source.value)) {
                                            currentSources.push(source.value);
                                        } else if (!checked && currentSources.includes(source.value)) {
                                            currentSources = currentSources.filter(v => v !== source.value);
                                        }
                                        setNewFormData({ ...newFormData, inventorySources: currentSources });
                                    }} 
                                />
                                <label className="ms-2 mb-0" htmlFor={`newChkSource_${source.value}`} style={{ cursor: 'pointer' }}>
                                    {source.label}
                                </label>
                            </div>
                        ))}
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div className="form-check form-switch">
                    <input
                      className={`form-check-input ${validationErrors.allowSubClient ? 'is-invalid' : ''}`}
                      type="checkbox"
                      id="newAllowSubClient"
                      checked={newFormData.allowSubClient || false}
                      onChange={(e) => {
                        setNewFormData({ ...newFormData, allowSubClient: e.target.checked });
                        setValidationErrors(prev => ({...prev, allowSubClient: ""}));
                      }}
                      disabled={!!(newFormData.parentId && newFormData.parentId.trim() !== "")}
                    />
                    <label className="form-check-label" htmlFor="newAllowSubClient">
                      Allow Sub Client
                      {newFormData.parentId && newFormData.parentId.trim() !== "" && (
                        <span className="text-muted ms-2">(Disabled when parent is selected)</span>
                      )}
                    </label>
                    {validationErrors.allowSubClient && <div className="invalid-feedback d-block">{validationErrors.allowSubClient}</div>}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={handleCloseNewModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveNew}
                      disabled={newLoading || !newFormData.clientName || !newFormData.phone || !newFormData.email || !newFormData.address1 || !newFormData.city || !newFormData.country || !newFormData.province || !newFormData.postcode || !newFormData.contactPerson || postcodeError}
                    >
                      {newLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Creating...
                        </>
                      ) : (
                        "Create Client"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* S3 Sync Configuration Modal */}
      {showS3SyncModal && currentS3SyncClientId && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", pointerEvents: "auto" }}
          onClick={(e) => {
            // Prevent modal from closing when clicking on backdrop
            // Only allow closing via Cancel button or X button
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          }}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configure S3 Sync Settings</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseS3SyncModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="storageSize" className="form-label">
                        Storage Size (G) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="storageSize"
                        value={s3SyncData.storageSize || ""}
                        onChange={(e) => setS3SyncData({ ...s3SyncData, storageSize: parseInt(e.target.value) || 0 })}
                        required
                        min="0"
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="maxFileSize" className="form-label">
                        Max File Size (M)
                        {!s3SyncData.syncIgnoreSize && <span className="text-danger">*</span>}
                      </label>
                      <input
                        type="number"
                        className={`form-control ${!s3SyncData.syncIgnoreSize && (!s3SyncData.maxFileSize || s3SyncData.maxFileSize <= 0) ? "is-invalid" : ""}`}
                        id="maxFileSize"
                        value={s3SyncData.maxFileSize || ""}
                        onChange={(e) => setS3SyncData({ ...s3SyncData, maxFileSize: e.target.value ? parseInt(e.target.value) : null })}
                        min="0"
                        required={!s3SyncData.syncIgnoreSize}
                        disabled={s3SyncData.syncIgnoreSize}
                      />
                      {!s3SyncData.syncIgnoreSize && (!s3SyncData.maxFileSize || s3SyncData.maxFileSize <= 0) && (
                        <div className="invalid-feedback">
                          Max File Size is required when Ignore Size is disabled
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="auditDownload"
                          checked={s3SyncData.auditDownload || false}
                          onChange={(e) => setS3SyncData({ ...s3SyncData, auditDownload: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="auditDownload">
                          Audit Download <span className="text-danger">*</span>
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="imageOnly"
                          checked={s3SyncData.imageOnly || false}
                          onChange={(e) => setS3SyncData({ ...s3SyncData, imageOnly: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="imageOnly">
                          Image Only <span className="text-danger">*</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="syncIgnoreTimeStamp"
                          checked={s3SyncData.syncIgnoreTimeStamp || false}
                          onChange={(e) => setS3SyncData({ ...s3SyncData, syncIgnoreTimeStamp: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="syncIgnoreTimeStamp">
                          Ignore Time Stamp <span className="text-danger">*</span>
                        </label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="syncIgnoreSize"
                          checked={s3SyncData.syncIgnoreSize || false}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setS3SyncData({
                              ...s3SyncData,
                              syncIgnoreSize: newValue,
                              // Clear maxFileSize when ignore size is enabled
                              maxFileSize: newValue ? null : s3SyncData.maxFileSize
                            });
                          }}
                        />
                        <label className="form-check-label" htmlFor="syncIgnoreSize">
                          Ignore Size <span className="text-danger">*</span>
                        </label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="manualSyncEnabled"
                          checked={s3SyncData.manualSyncEnabled || false}
                          onChange={(e) => setS3SyncData({ ...s3SyncData, manualSyncEnabled: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="manualSyncEnabled">
                          Manual Sync Enabled <span className="text-danger">*</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="enableDebugLogging"
                          checked={s3SyncData.enableDebugLogging || false}
                          onChange={(e) => setS3SyncData({ ...s3SyncData, enableDebugLogging: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="enableDebugLogging">
                          Enable Debug Logging <span className="text-danger">*</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-4 shadow-sm"
                  onClick={handleCloseS3SyncModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveS3Sync}
                  disabled={s3SyncLoading[currentS3SyncClientId]}
                >
                  {s3SyncLoading[currentS3SyncClientId] ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    "Save Configuration"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cloud Resources View Modal */}
      {showResourcesModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Cloud Resources</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowResourcesModal(false);
                    setResourcesData(null);
                    setCurrentResourcesClientId(null);
                  }}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {resourcesData ? (
                  <div>
                    {renderCopyableResourceField("S3 Bucket Name", resourcesData.bucketName)}
                    {renderCopyableResourceField("Cognito Identity Pool ID", resourcesData.identityPoolId)}
                    {renderCopyableResourceField("SNS Topic ARN", resourcesData.snsTopic)}
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-4 shadow-sm"
                  onClick={() => {
                    setShowResourcesModal(false);
                    setResourcesData(null);
                    setCurrentResourcesClientId(null);
                  }}
                >
                  Close
                </button>
                {currentUser && currentUser.username === "gu.hunter@gmail.com" &&
                  !resourcesData?.hasSubClients && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        setShowResourcesModal(false);
                        setShowDeleteConfirm(true);
                        setDeleteConfirmStage(1);
                        setDeleteConfirmCountdown(10);
                        // Start countdown timer
                        const interval = setInterval(() => {
                          setDeleteConfirmCountdown((prev) => {
                            if (prev <= 1) {
                              clearInterval(interval);
                              setDeleteCountdownInterval(null);
                              return 0;
                            }
                            return prev - 1;
                          });
                        }, 1000);
                        setDeleteCountdownInterval(interval);
                      }}
                      disabled={!resourcesData}
                    >
                      Delete Resources
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={(e) => {
            // Don't close on outside click
            if (e.target.className.includes("modal show d-block")) {
              e.stopPropagation();
            }
          }}
        >
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  {deleteConfirmStage === 1 ? "⚠️ First Confirmation Required" : "⚠️ Final Confirmation Required"}
                </h5>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger" role="alert">
                  <h6 className="alert-heading">
                    <strong>DANGEROUS OPERATION</strong>
                  </h6>
                  <p className="mb-2">
                    {deleteConfirmStage === 1
                      ? "You are about to delete all AWS cloud resources for this media client. This action cannot be undone!"
                      : "This is your final confirmation. Are you absolutely sure you want to proceed?"}
                  </p>
                  <p className="mb-0">
                    <strong>Resources to be deleted:</strong>
                  </p>
                  {(() => {
                    // Use the stored flag to determine if this is a child client
                    const isChildClient = isCurrentResourcesClientChild;

                    return (
                      <>
                        <ul className="mb-0 mt-2">
                          <li>Cognito Identity Pool</li>
                          <li>SNS Topic</li>
                          {isChildClient ? (
                            <li>EventBridge Rule (1 rule)</li>
                          ) : (
                            <li>EventBridge Rules (3 rules)</li>
                          )}
                          <li>IAM Role</li>
                        </ul>
                        {isChildClient ? (
                          <p className="mt-2 mb-0">
                            <strong>Note:</strong> The S3 bucket and folder contents will NOT be deleted (parent's bucket). Only AWS resources are deleted.
                          </p>
                        ) : (
                          <p className="mt-2 mb-0">
                            The S3 bucket will be configured with a 60-day expiration policy for all objects.
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
                {deleteConfirmCountdown > 0 && (
                  <div className="text-center mb-3">
                    <div className="display-6 text-danger">{deleteConfirmCountdown}</div>
                    <small className="text-muted">seconds remaining</small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-4 shadow-sm"
                  onClick={() => {
                    // Cancel - default action
                    if (deleteCountdownInterval) {
                      clearInterval(deleteCountdownInterval);
                      setDeleteCountdownInterval(null);
                    }
                    setShowDeleteConfirm(false);
                    setDeleteConfirmCountdown(10);
                    setDeleteConfirmStage(1);
                  }}
                  autoFocus
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={async () => {
                    if (deleteConfirmCountdown > 0) {
                      // Countdown not finished, do nothing
                      return;
                    }

                    if (deleteConfirmStage === 1) {
                      // First confirmation - show second confirmation
                      if (deleteCountdownInterval) {
                        clearInterval(deleteCountdownInterval);
                      }
                      setDeleteConfirmStage(2);
                      setDeleteConfirmCountdown(10);
                      // Start countdown timer for second confirmation
                      const interval = setInterval(() => {
                        setDeleteConfirmCountdown((prev) => {
                          if (prev <= 1) {
                            clearInterval(interval);
                            setDeleteCountdownInterval(null);
                            return 0;
                          }
                          return prev - 1;
                        });
                      }, 1000);
                      setDeleteCountdownInterval(interval);
                    } else {
                      // Second confirmation - proceed with deletion
                      if (deleteCountdownInterval) {
                        clearInterval(deleteCountdownInterval);
                        setDeleteCountdownInterval(null);
                      }
                      setShowDeleteConfirm(false);
                      setDeleteConfirmCountdown(10);
                      setDeleteConfirmStage(1);

                      // Call delete endpoint
                      setActionLoading({ ...actionLoading, [currentResourcesClientId]: true });
                      try {
                        // Use the stored flag to determine if this is a child client
                        if (isCurrentResourcesClientChild) {
                          await mediaClientService.deleteSubClientCloudResources(currentResourcesClientId);
                          toast.success("Sub client cloud resources deleted successfully.");
                        } else {
                          await mediaClientService.deleteCloudResources(currentResourcesClientId);
                          toast.success("Cloud resources deleted successfully. S3 bucket configured with 60-day expiration policy.");
                        }
                        // Refresh the media clients list
                        const response = await mediaClientService.getMediaClients(
                          currentPage,
                          pageSize,
                          activeSearchTerm,
                          sortDirection
                        );
                        setMediaClients(response.data.content);
                        setPagination({
                          totalElements: response.data.totalElements,
                          totalPages: response.data.totalPages,
                          currentPage: response.data.number,
                          pageSize: response.data.size,
                          isFirst: response.data.first,
                          isLast: response.data.last
                        });
                        // Close resources modal
                        setShowResourcesModal(false);
                        setResourcesData(null);
                        setCurrentResourcesClientId(null);
                        setIsCurrentResourcesClientChild(false);
                      } catch (error) {
                        const errorMessage =
                          (error.response && error.response.data) ||
                          error.message ||
                          "Failed to delete cloud resources";
                        console.error("Error deleting cloud resources:", error);
                        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
                      } finally {
                        setActionLoading({ ...actionLoading, [currentResourcesClientId]: false });
                      }
                    }
                  }}
                  disabled={deleteConfirmCountdown > 0 || actionLoading[currentResourcesClientId]}
                >
                  {actionLoading[currentResourcesClientId] ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : deleteConfirmStage === 1 ? (
                    "Confirm Delete"
                  ) : (
                    "Final Confirm Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media Settings Modal */}
      {showMediaSettingsModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", pointerEvents: "auto" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseMediaSettingsModal();
            }
          }}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Media Settings</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseMediaSettingsModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <PhotoBaseMediaSettingsModal
                  clientId={currentMediaSettingsClientId}
                  storage={currentMediaSettingsStorage}
                  settingsData={mediaSettingsData}
                  setSettingsData={setMediaSettingsData}
                  errors={mediaSettingsErrors}
                  setErrors={setMediaSettingsErrors}
                  onClose={handleCloseMediaSettingsModal}
                />
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

// Media Settings Modal Component
const PhotoBaseMediaSettingsModal = ({ clientId, storage, settingsData, setSettingsData, errors, setErrors, onClose }) => {
  const [saving, setSaving] = useState(false);

  const isValidFolderName = (name) => {
    if (!name || name.trim().length === 0) {
      return false;
    }
    const invalidChars = /[\\/:*?"<>|]/;
    if (invalidChars.test(name)) {
      return false;
    }
    for (let i = 0; i < name.length; i++) {
      if (name.charCodeAt(i) < 32) {
        return false;
      }
    }
    const upperName = name.toUpperCase();
    const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/;
    if (reservedNames.test(upperName)) {
      return false;
    }
    if (name.endsWith('.') || name.endsWith(' ')) {
      return false;
    }
    if (name.length > 255) {
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    const names = settingsData.subFoldersInput.split(';').map(s => s.trim());
    const filtered = names.filter(s => s.length > 0);

    if (filtered.length > 0 && filtered.some(name => name && !isValidFolderName(name))) {
      setErrors({ ...errors, folderError: "Invalid folder name detected. Folder names cannot contain / \\ : * ? \" < > |, cannot be reserved names (CON, PRN, AUX, NUL, COM1-9, LPT1-9), cannot end with dot or space, cannot exceed 255 characters, and cannot contain control characters." });
      return;
    }

    if (filtered.length > 0) {
      const lowerCaseNames = filtered.map(name => name.toLowerCase());
      const uniqueNames = new Set(lowerCaseNames);
      if (uniqueNames.size !== lowerCaseNames.length) {
        setErrors({ ...errors, folderError: "Duplicate folder names detected. Folder names are case-insensitive and must be unique." });
        return;
      }
    }

    if (settingsData.rootFolder && !isValidFolderName(settingsData.rootFolder)) {
      setErrors({ ...errors, rootFolderError: "Invalid root folder name. Folder name cannot contain / \\ : * ? \" < > |, cannot be reserved names (CON, PRN, AUX, NUL, COM1-9, LPT1-9), cannot end with dot or space, cannot exceed 45 characters, and cannot contain control characters." });
      return;
    }

    setErrors({ ...errors, folderError: "", photoPixelLimitError: "", photoSizeLimitError: "", audioNoteLengthError: "", rootFolderError: "" });

    if (settingsData.photoPixelLimitEnabled && (!settingsData.photoPixelLimit || parseInt(settingsData.photoPixelLimit) <= 0)) {
      setErrors({ ...errors, photoPixelLimitError: "Photo pixel limit must be a positive number when enabled." });
      return;
    }

    if (settingsData.photoSizeLimitEnabled && (!settingsData.photoSizeLimit || parseFloat(settingsData.photoSizeLimit) < 0.1)) {
      setErrors({ ...errors, photoSizeLimitError: "Photo size limit must be at least 0.1 when enabled." });
      return;
    }

    // Validate audio note length (always required)
    if (!settingsData.audioNoteLength || parseInt(settingsData.audioNoteLength) < 10 || parseInt(settingsData.audioNoteLength) > 1800) {
      setErrors({ ...errors, audioNoteLengthError: "Audio note length must be between 10 and 1800 seconds." });
      return;
    }

    setSaving(true);
    try {
      // Save using original casing
      await clientService.savePhotoBaseMediaSettings(clientId, {
        personalFolderEnabled: settingsData.personalFolderEnabled,
        subFolders: filtered.join(';'), // send with original casing
        photoPixelLimit: settingsData.photoPixelLimitEnabled ? parseInt(settingsData.photoPixelLimit) || null : null,
        photoSizeLimit: settingsData.photoSizeLimitEnabled ? parseFloat(settingsData.photoSizeLimit) || null : null,
        photoTimeStampEnabled: settingsData.photoTimeStampEnabled,
        audioNoteLength: parseInt(settingsData.audioNoteLength) || null,
        rootFolder: storage === "Google Drive" ? null : (settingsData.rootFolder || null)
      });
      setSettingsData({ ...settingsData, subFoldersInput: filtered.join(';') });
      if (settingsData.subFolderEnabled && filtered.length === 0) {
        setSettingsData({ ...settingsData, subFolderEnabled: false });
      }
      toast.success("Media settings saved successfully");
      onClose();
    } catch (error) {
      const errorMessage =
        (error.response && error.response.data) ||
        error.message ||
        "Failed to save media settings";
      console.error("Error saving media settings:", error);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form>
      {/* Personal Folder */}
      <div className="mb-3">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="personalFolder"
            checked={settingsData.personalFolderEnabled}
            onChange={(e) => setSettingsData({ ...settingsData, personalFolderEnabled: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="personalFolder">
            Personal Folder
          </label>
        </div>
      </div>

      {/* Subfolders */}
      <div className="mb-3">
        <div className="form-check form-switch mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="subfolders"
            checked={settingsData.subFolderEnabled}
            onChange={(e) => {
              setSettingsData({
                ...settingsData,
                subFolderEnabled: e.target.checked,
                subFoldersInput: e.target.checked ? settingsData.subFoldersInput : ""
              });
              setErrors({ ...errors, folderError: "" });
            }}
          />
          <label className="form-check-label" htmlFor="subfolders">
            Subfolders
          </label>
        </div>
        <textarea
          className="form-control"
          disabled={!settingsData.subFolderEnabled}
          value={settingsData.subFoldersInput}
          onChange={(e) => {
            setSettingsData({ ...settingsData, subFoldersInput: e.target.value });
            setErrors({ ...errors, folderError: "" });
          }}
          placeholder="Enter folder names separated by semicolons (;)"
          rows={3}
        />
        {errors.folderError && <div className="text-danger small mt-1">{errors.folderError}</div>}
      </div>

      {/* Root Folder - only for non-Google Drive */}
      {storage !== "Google Drive" && (
        <div className="mb-3">
          <label className="form-label">Root Folder (Optional)</label>
          <input
            type="text"
            className={`form-control ${errors.rootFolderError ? 'is-invalid' : ''}`}
            value={settingsData.rootFolder}
            onChange={(e) => {
              setSettingsData({ ...settingsData, rootFolder: e.target.value });
              setErrors({ ...errors, rootFolderError: "" });
            }}
            placeholder="Enter root folder name"
            maxLength="45"
          />
          {errors.rootFolderError && <div className="invalid-feedback">{errors.rootFolderError}</div>}
        </div>
      )}

      {/* Photo Pixel Limit */}
      <div className="mb-3">
        <div className="form-check form-switch mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="photoPixelLimitEnabled"
            checked={settingsData.photoPixelLimitEnabled}
            onChange={(e) => {
              setSettingsData({
                ...settingsData,
                photoPixelLimitEnabled: e.target.checked,
                photoPixelLimit: e.target.checked ? settingsData.photoPixelLimit : ""
              });
              setErrors({ ...errors, photoPixelLimitError: "" });
            }}
          />
          <label className="form-check-label" htmlFor="photoPixelLimitEnabled">
            Photo Pixel Limit
          </label>
        </div>
        <input
          type="number"
          className={`form-control ${errors.photoPixelLimitError ? "is-invalid" : ""}`}
          value={settingsData.photoPixelLimit}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || (parseInt(value) > 0)) {
              setSettingsData({ ...settingsData, photoPixelLimit: value });
              setErrors({ ...errors, photoPixelLimitError: "" });
            }
          }}
          disabled={!settingsData.photoPixelLimitEnabled}
          placeholder="Enter pixel limit"
          min="1"
        />
        <small className="text-muted">Maximum pixel dimensions for uploaded photos (e.g., 1920 for 1920x1080), 2048 is the default if not set</small>
        {errors.photoPixelLimitError && (
          <div className="invalid-feedback">{errors.photoPixelLimitError}</div>
        )}
      </div>

      {/* Photo Size Limit */}
      <div className="mb-3">
        <div className="form-check form-switch mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="photoSizeLimitEnabled"
            checked={settingsData.photoSizeLimitEnabled}
            onChange={(e) => {
              setSettingsData({
                ...settingsData,
                photoSizeLimitEnabled: e.target.checked,
                photoSizeLimit: e.target.checked ? settingsData.photoSizeLimit : ""
              });
              setErrors({ ...errors, photoSizeLimitError: "" });
            }}
          />
          <label className="form-check-label" htmlFor="photoSizeLimitEnabled">
            Photo Size Limit
          </label>
        </div>
        <input
          type="number"
          className={`form-control ${errors.photoSizeLimitError ? "is-invalid" : ""}`}
          value={settingsData.photoSizeLimit}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || (parseFloat(value) >= 0.1)) {
              setSettingsData({ ...settingsData, photoSizeLimit: value });
              setErrors({ ...errors, photoSizeLimitError: "" });
            }
          }}
          disabled={!settingsData.photoSizeLimitEnabled}
          placeholder="Enter size limit"
          min="0.1"
          step="0.1"
        />
        <small className="text-muted">Maximum file size in megabytes for uploaded photos (minimum 0.1 MB), 0.4MB is the default if not set</small>
        {errors.photoSizeLimitError && (
          <div className="invalid-feedback">{errors.photoSizeLimitError}</div>
        )}
      </div>

      {/* Photo Time Stamp */}
      <div className="mb-3">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="photoTimeStampEnabled"
            checked={settingsData.photoTimeStampEnabled}
            onChange={(e) => setSettingsData({ ...settingsData, photoTimeStampEnabled: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="photoTimeStampEnabled">
            Photo Filename Timestamp
          </label>
        </div>
        <small className="text-muted">Include timestamp in photo filename (e.g., nnnn_mm-dd-yyyy_hh-mm-ss.jpg)</small>
      </div>

      {/* Audio Note Length */}
      <div className="mb-3">
        <label htmlFor="audioNoteLength" className="form-label">
          Audio Note Length
        </label>
        <input
          type="number"
          id="audioNoteLength"
          className={`form-control ${errors.audioNoteLengthError ? "is-invalid" : ""}`}
          value={settingsData.audioNoteLength}
          onChange={(e) => {
            const value = e.target.value;
            setSettingsData({ ...settingsData, audioNoteLength: value });
            setErrors({ ...errors, audioNoteLengthError: "" });
          }}
          placeholder="Enter length in seconds"
          min="10"
          max="1800"
        />
        <small className="text-muted">Maximum length for audio notes in seconds (10-1800). If not set or less than 10, defaults to property value (capped at 1800 seconds)</small>
        {errors.audioNoteLengthError && (
          <div className="invalid-feedback">{errors.audioNoteLengthError}</div>
        )}
      </div>

      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-secondary rounded-pill px-4 shadow-sm"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : (
            "Save Settings"
          )}
        </button>
      </div>
    </form>
  );
};

export default MediaClientManager;

